import type {ShellAccount} from '@wandb/forge-shell-contract';
import React, {useEffect, useRef, useState} from 'react';
import {useLocation} from 'react-router';

import {isCanonicalProfilePath} from '../../../routes/utils';
import {useShellAccountContext} from '../../../shell/ShellAccountContext';
import {isMountedInShell} from '../../../shellRemote';
import {useLastWhen} from '../../../util/hooks/useLastWhen';
import {AccountSelectorContext} from './AccountSelectorContextProvider';
import {AccountSelectorContextProviderWrapper} from './AccountSelectorContextProviderWrapper';
import {getCanSeeOrgInURL, getUrlAccount} from './AccountSelectorUtils';
import {ShellAdminModals} from './ShellAdminModals';
import {type Account, AccountType} from './types';
import {useUrlAccountInputs} from './useUrlAccountInputs';

export function findShellAccount(
  shellAccount: ShellAccount,
  accountList: Account[]
): Account | undefined {
  return (
    accountList.find(account => account.id === shellAccount.id) ??
    accountList.find(
      account =>
        account.name === shellAccount.name &&
        (shellAccount.kind === 'personal'
          ? account.accountType === AccountType.Personal
          : account.accountType !== AccountType.Personal)
    )
  );
}

// Under the Forge shell the shell owns account selection: this provider
// broadcasts which account the URL names, reads the selection back from the
// mount props, and never touches storage.
export const ShellAccountSelectorContextProvider: React.FC<{
  children?: React.ReactNode;
}> = ({children}) => {
  const {
    selectedAccount: shellAccount,
    broadcastUrlAccount,
    selectAccount: shellSelectAccount,
  } = useShellAccountContext();
  const {
    viewer,
    accountList,
    isAccountListLoading,
    userInfo,
    orgName,
    entityName,
    orgQuery,
    entityQuery,
    personalEntityUserID,
    isAdminMode,
    isProfilePath,
    isLoaded,
  } = useUrlAccountInputs();

  // A /profile/:entityName URL names a user, not an account, so the header
  // pick keeps scoping the page.
  const {pathname} = useLocation();
  const isUserProfileUrl = isCanonicalProfilePath(pathname);

  // The account this page's URL names; undefined until resolution inputs load.
  const resolvedUrlAccount =
    isLoaded && viewer != null
      ? getUrlAccount({
          selectedAccount: null,
          lastSelectedAccount: null,
          isProfilePath,
          isAdminMode,
          viewer,
          accountList,
          userInfo,
          orgName,
          entityName,
          orgQuery,
          entityQuery,
          personalEntityUserID,
        })
      : undefined;
  const urlAccount =
    isUserProfileUrl && resolvedUrlAccount !== undefined
      ? null
      : resolvedUrlAccount;

  // Broadcast the URL account once per payload so the shell can apply its
  // precedence (URL > header pick > storage) without an extra query.
  const lastBroadcastRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (broadcastUrlAccount == null || urlAccount === undefined) {
      return;
    }
    const payload: ShellAccount | null =
      urlAccount?.id != null
        ? {
            id: urlAccount.id,
            name: urlAccount.name,
            kind:
              urlAccount.accountType === AccountType.Personal
                ? 'personal'
                : 'organization',
            ...(urlAccount.isDummyAccount ? {isDummyAccount: true} : {}),
          }
        : null;
    const key = JSON.stringify(payload);
    if (lastBroadcastRef.current === key) {
      return;
    }
    lastBroadcastRef.current = key;
    broadcastUrlAccount(payload);
  }, [broadcastUrlAccount, urlAccount]);

  // The shell's selection mapped back to an app Account. A member account
  // resolves against the viewer's list; a dummy we broadcast resolves to the
  // URL account we already built (which carries plan info the shell lacks).
  let shellResolvedAccount: Account | null;
  if (shellAccount == null) {
    shellResolvedAccount = null;
  } else if (!shellAccount.isDummyAccount) {
    shellResolvedAccount = findShellAccount(shellAccount, accountList) ?? null;
  } else if (urlAccount?.id === shellAccount.id) {
    shellResolvedAccount = urlAccount;
  } else {
    shellResolvedAccount = null;
  }

  const [override, setOverride] = useState<{
    forShellAccountId: string | undefined;
    account: Account | null;
  } | null>(null);
  // The shell owns selection and storage, so a local pick is only a
  // render-level override keyed by the shell account it was made against.
  const setSelectedAccount = (account: Account | null) => {
    // Member accounts also go to the shell so the pick survives a redirect;
    // the override still applies immediately so the app does not wait for
    // the shell to round-trip the selection.
    if (
      account?.id != null &&
      !account.isDummyAccount &&
      shellSelectAccount != null
    ) {
      shellSelectAccount({
        id: account.id,
        name: account.name,
        kind:
          account.accountType === AccountType.Personal
            ? 'personal'
            : 'organization',
      });
    }
    setOverride({forShellAccountId: shellAccount?.id, account});
  };
  const overrideApplies =
    override != null && override.forShellAccountId === shellAccount?.id;
  const selectedAccount = overrideApplies
    ? override.account
    : // A shell without `broadcastUrlAccount` never hears the URL broadcast, so this
      // provider applies the URL account itself under that rollout gap.
      broadcastUrlAccount == null
      ? (urlAccount ?? shellResolvedAccount)
      : shellResolvedAccount;

  // Hold the last resolved selection while a navigation reloads the URL
  // inputs, so consumers (e.g. ARIA's availability) don't see a transient null.
  const publishedAccount = useLastWhen<Account | null>(
    selectedAccount,
    isLoaded,
    null
  );

  const canSeeOrgInURL = isLoaded
    ? getCanSeeOrgInURL({
        selectedAccount: null,
        lastSelectedAccount: null,
        isProfilePath,
        isAdminMode,
        viewer: viewer!,
        accountList,
        userInfo,
        orgName,
        entityName,
        orgQuery,
        entityQuery,
      })
    : true;

  const value = {
    selectedAccount: publishedAccount,
    setSelectedAccount,
    lastNonImpersonatedAccount: null,
    setLastNonImpersonatedAccount: () => {},
    canSeeOrgInURL,
    accountList,
    isAccountListLoading,
  };

  // React Compiler caches the context value; this component must stay
  // compiled for that to hold.
  return (
    // oxlint-disable-next-line react/jsx-no-constructed-context-values
    <AccountSelectorContext.Provider value={value}>
      {children}
    </AccountSelectorContext.Provider>
  );
};

export const ShellAccountSelectorContextProviderWrapper: React.FC<{
  children?: React.ReactNode;
}> = ({children}) => (
  <ShellAccountSelectorContextProvider>
    <ShellAdminModals />
    {children}
  </ShellAccountSelectorContextProvider>
);

export const AppAccountSelectorProvider: React.FC<{
  children?: React.ReactNode;
}> = ({children}) =>
  isMountedInShell() ? (
    <ShellAccountSelectorContextProviderWrapper>
      {children}
    </ShellAccountSelectorContextProviderWrapper>
  ) : (
    <AccountSelectorContextProviderWrapper>
      {children}
    </AccountSelectorContextProviderWrapper>
  );
